import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
/**
 * THE DARK PALETTE LIVES ONLY IN THIS FILE, which is why the import is not
 * optional even though it looks it.
 *
 * Every token from `@ridgewill/brand` is `var(--rw-x, <light value>)`, so this
 * portal rendered in correct corporate colours for months without ever loading
 * the stylesheet. A `var()` fallback can hold one value and a theme needs two,
 * so dark ships here and nowhere else.
 *
 * Delete this line and nothing breaks — which is the danger. The portal stays
 * light, the theme control in the sidebar keeps switching a setting that no
 * longer changes anything, and there is no error anywhere to explain it.
 *
 * Imported BEFORE `styles.css`, which consumes these custom properties.
 */
import '@ridgewill/brand/tokens.css';
import './styles.css';
import { App } from './App.js';
import { completeCallbacks, fleetSession, supportSession } from './lib/fleet-token.js';

/**
 * StrictMode is ON, and the portal this replaces turned it off.
 *
 * It was disabled there after an exception that turned out not to be a React or
 * Next defect at all: `Overview.tsx` guarded its values with `isLoading ? '—' :
 * d!.stats…`, and `isLoading` is false in states where `data` is still
 * undefined. StrictMode's remount produces exactly such a state, so the non-null
 * assertion threw — and the same state occurs without StrictMode whenever that
 * query errors. It was a production white-screen waiting for the API to have a
 * bad day.
 *
 * StrictMode found a real bug and was blamed for it. It stays on, and the
 * screens here do not use non-null assertions on fetched data.
 */
/**
 * A fleet-api or support sign-in round trip lands on `/callback`
 * (lib/fleet-token.ts), and it is finished BEFORE the first render. The two
 * share the callback; `completeCallbacks` gives the response to the session
 * that started it. The exchange puts the address bar back
 * on the screen that asked; rendering first would draw that screen for a URL
 * that is still `/callback`, with a fetch racing the token it needs.
 *
 * Everywhere else `completeCallback` returns at once, and it never rejects.
 */
void completeCallbacks([fleetSession, supportSession]).then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
